import { useState } from 'react'
import { Link } from 'react-router'
import { ChartCard } from '@/components/patterns/ChartCard'
import { Button } from '@/components/ui/button'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { CategoryChip } from '../components/CategoryChip'
import type { AgreementParty } from '../agreements/types'
import { relationHref } from './links'
import type { WithoutAgreement } from './types'

const COLLAPSED = 5

/** Widget 7: active relations without an active agreement (design §4.1); shows 5 and unfolds in place. */
export function WithoutAgreementWidget({
  rows,
  total,
  onNewAgreement,
}: {
  rows: WithoutAgreement[]
  total: number
  onNewAgreement: (party: AgreementParty) => void
}) {
  const [all, setAll] = useState(false)
  const shown = all ? rows : rows.slice(0, COLLAPSED)
  return (
    <ChartCard
      title={`Zonder afspraak of ritme (${total})`}
      definition="Actieve relaties zonder lopende afspraak"
      empty={rows.length === 0 ? { title: 'Alle relaties hebben een afspraak.' } : null}
    >
      <ul className="divide-y divide-border">
        {shown.map((row) => (
          <li key={`${row.type}:${row.id}`} className="flex items-center gap-3 py-2">
            <EntityAvatar kind={row.type === 'contact' ? 'person' : 'company'} name={row.name} size="sm" />
            <span className="flex min-w-0 flex-1 flex-col">
              <Link to={relationHref(row.type, row.id, null)} className="truncate text-sm font-medium underline-offset-2 hover:underline">
                {row.name}
              </Link>
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                {row.context_name ? <span className="truncate">{row.context_name}</span> : null}
                {row.category ? <CategoryChip category={row.category} /> : null}
              </span>
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              aria-label={`Afspraak voor ${row.name}`}
              onClick={() => onNewAgreement({ kind: row.type, id: row.id, name: row.name })}
            >
              + Afspraak
            </Button>
          </li>
        ))}
      </ul>
      {rows.length > COLLAPSED ? (
        <Button type="button" variant="ghost" size="sm" className="self-start" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? 'Toon minder' : `Toon alle ${total}`}
        </Button>
      ) : null}
    </ChartCard>
  )
}
